import { useEffect, useRef, useState } from "react";

/**
 * A plain, minimal volume slider built from scratch instead of a native
 * <input type="range">. Native sliders always leave a visual gap at each end
 * (the round thumb's center reaches the edge, so half the thumb overhangs
 * past the track) — that's normal browser behavior, not a bug, but it reads
 * as "can't reach the end." Building the track/fill/thumb ourselves gives
 * pixel-exact control so the fill genuinely spans edge-to-edge.
 */
export default function VolumeSlider({ value, onChange, testId, className = "" }) {
  const trackRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const updateFromClientX = (clientX) => {
    const track = trackRef.current;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    onChange(Math.round(ratio * 20) / 20); // snap to 0.05 steps
  };

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => updateFromClientX(e.touches ? e.touches[0].clientX : e.clientX);
    const onUp = () => setDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragging]);

  const pct = Math.round(value * 100);

  return (
    <div
      ref={trackRef}
      onMouseDown={(e) => { e.stopPropagation(); setDragging(true); updateFromClientX(e.clientX); }}
      onTouchStart={(e) => { e.stopPropagation(); setDragging(true); updateFromClientX(e.touches[0].clientX); }}
      role="slider"
      aria-label="Volume"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "ArrowRight" || e.key === "ArrowUp") onChange(Math.min(1, Math.round((value + 0.05) * 20) / 20));
        if (e.key === "ArrowLeft" || e.key === "ArrowDown") onChange(Math.max(0, Math.round((value - 0.05) * 20) / 20));
      }}
      className={`relative h-4 flex items-center cursor-pointer flex-shrink-0 ${className}`}
      data-testid={testId}
    >
      <div className="absolute left-0 right-0 h-[3px] rounded-full bg-white/20" />
      <div className="absolute left-0 h-[3px] rounded-full bg-[#FF8090]" style={{ width: `${pct}%` }} />
      <div
        className="absolute w-2.5 h-2.5 rounded-full bg-[#FF8090]"
        style={{ left: `calc(${pct}% - 5px)` }}
      />
    </div>
  );
}
